import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { featureAPI, folderAPI } from '../../../api/services';

interface TreeNode {
  id: string;
  type: 'folder' | 'feature' | 'task';
  name: string;
  status?: string;
  data: any;
  children: TreeNode[];
}

interface Props {
  projectId: string;
  tasks: any[];
  onSelectFolder?: (folderId: string) => void;
  onSelectFeature?: (featureId: string) => void;
}

function buildTree(folders: any[], features: any[], tasks: any[]): TreeNode[] {
  const safeFolders = Array.isArray(folders) ? folders : [];
  const safeFeatures = Array.isArray(features) ? features : [];
  const safeTasks = Array.isArray(tasks) ? tasks : [];

  // Build folder nodes keyed by id
  const folderMap = new Map<string, TreeNode>();
  for (const f of safeFolders) {
    folderMap.set(f._id, {
      id: f._id,
      type: 'folder',
      name: f.name ?? 'Untitled Folder',
      data: f,
      children: [],
    });
  }

  // Nest child folders under parents
  const rootFolders: TreeNode[] = [];
  for (const f of safeFolders) {
    const node = folderMap.get(f._id)!;
    if (f.parentId && f.parentType === 'Folder' && folderMap.has(f.parentId)) {
      folderMap.get(f.parentId)!.children.push(node);
    } else {
      rootFolders.push(node);
    }
  }

  // Build feature nodes keyed by id
  const featureMap = new Map<string, TreeNode>();
  for (const ft of safeFeatures) {
    featureMap.set(ft._id, {
      id: ft._id,
      type: 'feature',
      name: ft.name ?? 'Untitled Feature',
      data: ft,
      children: [],
    });
  }

  // Place features under folders or at root
  const rootFeatures: TreeNode[] = [];
  for (const ft of safeFeatures) {
    const node = featureMap.get(ft._id)!;
    if (ft.folderId && folderMap.has(ft.folderId)) {
      folderMap.get(ft.folderId)!.children.push(node);
    } else {
      rootFeatures.push(node);
    }
  }

  // Build task nodes and place them
  const rootTasks: TreeNode[] = [];
  for (const t of safeTasks) {
    const taskNode: TreeNode = {
      id: t._id,
      type: 'task',
      name: t.title ?? t.name ?? 'Untitled Task',
      status: t.status,
      data: t,
      children: [],
    };

    if (t.parentType === 'feature' && t.parentId && featureMap.has(t.parentId)) {
      featureMap.get(t.parentId)!.children.push(taskNode);
    } else if (t.parentType === 'folder' && t.parentId && folderMap.has(t.parentId)) {
      folderMap.get(t.parentId)!.children.push(taskNode);
    } else {
      rootTasks.push(taskNode);
    }
  }

  // Sort helper: folders first, then features, then tasks
  const typeOrder = { folder: 0, feature: 1, task: 2 };
  const sortNodes = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => typeOrder[a.type] - typeOrder[b.type]);
    for (const n of nodes) {
      if (n.children.length > 0) sortNodes(n.children);
    }
  };

  const tree = [...rootFolders, ...rootFeatures, ...rootTasks];
  sortNodes(tree);
  return tree;
}

const TreeNodeRow: React.FC<{ node: TreeNode; depth: number; onSelectFolder?: (id: string) => void; onSelectFeature?: (id: string) => void }> = ({ node, depth, onSelectFolder, onSelectFeature }) => {
  const [expanded, setExpanded] = useState(node.type !== 'task');
  const hasChildren = node.children.length > 0;
  const isExpandable = hasChildren || node.type === 'folder'; // folders always expandable
  const navigate = useNavigate();

  const handleClick = () => {
    if (node.type === 'task') navigate(`/employee/tasks/${node.id}`);
    else if (node.type === 'folder' && onSelectFolder) onSelectFolder(node.id);
    else if (node.type === 'feature' && onSelectFeature) onSelectFeature(node.id);
  };

  return (
    <>
      <div
        className="flex items-center gap-2 py-1.5 px-2 hover:bg-gray-50 rounded cursor-pointer group"
        style={{ paddingLeft: `${depth * 20 + 8}px` }}
      >
        {/* Expand/collapse chevron */}
        {isExpandable ? (
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
            className="w-4 h-4 text-gray-400 hover:text-gray-600 shrink-0"
          >
            <svg
              className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        ) : (
          <span className="w-4 shrink-0" />
        )}

        {/* Type icon */}
        {node.type === 'folder' && (
          <svg className="w-4 h-4 text-blue-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
          </svg>
        )}
        {node.type === 'feature' && (
          <svg className="w-4 h-4 text-purple-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M14.25 6.087c0-.355.186-.676.401-.959.221-.29.349-.634.349-1.003 0-1.036-1.007-1.875-2.25-1.875s-2.25.84-2.25 1.875c0 .369.128.713.349 1.003.215.283.401.604.401.959v0a.64.64 0 01-.657.643 48.491 48.491 0 01-4.163-.3c.186 1.613.293 3.25.315 4.907a.656.656 0 01-.658.663v0c-.355 0-.676-.186-.959-.401a1.647 1.647 0 00-1.003-.349c-1.036 0-1.875 1.007-1.875 2.25s.84 2.25 1.875 2.25c.369 0 .713-.128 1.003-.349.283-.215.604-.401.959-.401v0c.31 0 .555.26.532.57a48.039 48.039 0 01-.642 5.056c1.518.19 3.058.309 4.616.354a.64.64 0 00.657-.643v0c0-.355-.186-.676-.401-.959a1.647 1.647 0 01-.349-1.003c0-1.035 1.008-1.875 2.25-1.875 1.243 0 2.25.84 2.25 1.875 0 .369-.128.713-.349 1.003-.215.283-.4.604-.4.959v0c0 .333.277.599.61.58a48.1 48.1 0 005.427-.63 48.05 48.05 0 00.582-4.717.532.532 0 00-.533-.57v0c-.355 0-.676.186-.959.401-.29.221-.634.349-1.003.349-1.035 0-1.875-1.007-1.875-2.25s.84-2.25 1.875-2.25c.37 0 .713.128 1.003.349.283.215.604.401.96.401v0a.656.656 0 00.658-.663 48.422 48.422 0 00-.37-5.36c-1.886.342-3.81.574-5.766.689a.578.578 0 01-.61-.58v0z" />
          </svg>
        )}
        {node.type === 'task' && (
          <span className={`w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center ${
            node.status === 'done' ? 'border-green-500 bg-green-500' :
            node.status === 'in_progress' ? 'border-blue-500' :
            node.status === 'blocked' ? 'border-red-500' :
            node.status === 'review' ? 'border-yellow-500' :
            'border-gray-300'
          }`}>
            {node.status === 'done' && (
              <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
            )}
          </span>
        )}

        {/* Name */}
        <span
          onClick={handleClick}
          className={`text-sm truncate flex-1 ${
            node.type === 'task' && node.status === 'done' ? 'text-gray-400 line-through' : 'text-gray-900'
          } hover:text-blue-600`}
          title={`${node.name}${node.data.description ? '\n' + node.data.description.slice(0, 200) : ''}`}
        >
          {node.name}
        </span>

        {/* Metadata badges */}
        {node.type === 'feature' && node.data.approvalStatus && node.data.approvalStatus !== 'not_required' && (
          <span className={`text-xs px-1.5 py-0.5 rounded-full shrink-0 ${
            node.data.approvalStatus === 'approved' ? 'bg-green-100 text-green-700' :
            node.data.approvalStatus === 'pending_approval' ? 'bg-yellow-100 text-yellow-700' :
            'bg-red-100 text-red-700'
          }`}>{node.data.approvalStatus.replace('_', ' ')}</span>
        )}
        {node.type === 'feature' && node.data.storyPoints > 0 && (
          <span className="text-xs text-blue-600 font-medium shrink-0">{node.data.storyPoints} SP</span>
        )}
        {node.type === 'feature' && node.data.stats && (
          <span className="text-xs text-gray-400 shrink-0">{node.data.stats.completedTasks}/{node.data.stats.totalTasks}</span>
        )}
        {node.type === 'task' && node.data.priority && (
          <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 ${
            node.data.priority === 'critical' ? 'bg-red-100 text-red-700' :
            node.data.priority === 'high' ? 'bg-orange-100 text-orange-700' :
            node.data.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
            'bg-gray-100 text-gray-600'
          }`}>{node.data.priority}</span>
        )}
        {node.type === 'task' && node.data.storyPoints > 0 && (
          <span className="text-xs text-blue-600 font-medium shrink-0">{node.data.storyPoints} SP</span>
        )}
        {node.type === 'task' && node.data.assignedTo?.[0]?.name && (
          <span className="text-xs text-gray-400 shrink-0">{node.data.assignedTo[0].name}</span>
        )}
        {node.type === 'folder' && (
          <span className="text-xs text-gray-400 shrink-0">{node.children.length > 0 ? `${node.children.length} items` : 'empty'}</span>
        )}
      </div>
      {expanded && hasChildren && node.children.map(child => (
        <TreeNodeRow key={child.id} node={child} depth={depth + 1} onSelectFolder={onSelectFolder} onSelectFeature={onSelectFeature} />
      ))}
      {expanded && !hasChildren && node.type === 'folder' && (
        <div className="text-xs text-gray-400 italic" style={{ paddingLeft: `${(depth + 1) * 20 + 28}px` }}>
          Empty — click to open folder
        </div>
      )}
    </>
  );
};

const ProjectWorkTree: React.FC<Props> = ({ projectId, tasks, onSelectFolder, onSelectFeature }) => {
  const [features, setFeatures] = useState<any[]>([]);
  const [folders, setFolders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      featureAPI.getByProject(projectId).then(r => r.data.data ?? []).catch(() => []),
      folderAPI.getByProject(projectId).then(r => r.data.data ?? []).catch(() => []),
    ]).then(([f, d]) => {
      setFeatures(Array.isArray(f) ? f : []);
      setFolders(Array.isArray(d) ? d : []);
    }).finally(() => setLoading(false));
  }, [projectId]);

  const tree = useMemo(() => buildTree(folders, features, tasks), [folders, features, tasks]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    );
  }

  if (tree.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400 text-sm">
        No work items yet
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Work Items</h3>
      <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
        {tree.map(node => (
          <TreeNodeRow key={node.id} node={node} depth={0} onSelectFolder={onSelectFolder} onSelectFeature={onSelectFeature} />
        ))}
      </div>
    </div>
  );
};

export default ProjectWorkTree;
